import { useEnum } from "@/api/services/global/enum";
import { createRenewVisaDocumentGroupApi, updateExpectSubmitDateApi } from "@/api/services/main/renew-visa";
import { TGetRenewVisaDocumentListOutputDto } from "@/api/services/main/renew-visa/dtos/get-renew-visa-document-list.dto";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Linkable } from "@/components/elements/Linkable";
import { TableSorting } from "@/components/elements/Table";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_FORMAT, DATE_NOT_TIMEZONE_FORMAT } from "@/constants/datetime";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { RenewVisaDocumentStatusTag } from "@/features/renew-visa/components/RenewVisaDocumentStatusTag";
import { RenewVisaNoteTextArea } from "@/features/renew-visa/components/RenewVisaNoteTextArea";
import { AppRoutes } from "@/routes/config";
import { button, text } from "@/theme/colors";
import { RENEW_VISA_DOCUMENT_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { findLabelByValue } from "@/utils/object";
import { truncateWithEllipsis } from "@/utils/string";
import { toast } from "@/utils/toast";
import { getDynamicRoute } from "@/utils/url";
import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

type UseMakeRenewVisaColumnsReturn = MRT_ColumnDef<TGetRenewVisaDocumentListOutputDto>[];

export const useMakeRenewVisaColumns = (): UseMakeRenewVisaColumnsReturn => {
  const { data: renewVisaDocumentStatuses } = useEnum("ERenewVisaDocumentStatus");
  const queryClient = useQueryClient();
  const { data: genderOptions } = useEnum("EGender");

  const navigate = useNavigate();

  const handleChangeExpectSubmitDate = async (renewVisaDocumentId: number, day: Dayjs) => {
    try {
      const rs = await updateExpectSubmitDateApi(renewVisaDocumentId, { expectSubmitDate: day.format(DATE_NOT_TIMEZONE_FORMAT) });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_LIST] });
    } catch (error) {
      showError(error);
    }
  };

  const columns = useMemo<MRT_ColumnDef<TGetRenewVisaDocumentListOutputDto>[]>(
    () => [
      {
        header: "外国人名",
        Header: <TableSorting name="employee.fullName">外国人名</TableSorting>,
        Cell: ({ row }) => {
          const { employeeFullName, employeeAvatarFilePath, employeeId } = row.original;
          return (
            <Linkable
              sx={{ "&:hover": { textDecoration: "underline" } }}
              width="fit-content"
              href={getDynamicRoute(AppRoutes.employeeDetail, { id: employeeId })}
            >
              <Box display="flex" alignItems="center">
                <Avatar filePath={employeeAvatarFilePath} alt="avatar" width={30} height={40} />
                {employeeFullName ? <Typography variant="body14"> {truncateWithEllipsis(employeeFullName, 26)}</Typography> : EMPTY_STR.TEXT}
              </Box>
            </Linkable>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "国籍",
        Header: <TableSorting name="employee.nationalityId">国籍</TableSorting>,
        Cell: ({ row }) => row.original.employeeNationalityName || EMPTY_STR.TEXT,
        size: 100,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "性別",
        Header: <TableSorting name="employee.gender">性別</TableSorting>,
        Cell: ({ row }) => findLabelByValue(genderOptions, row.original.employeeGender) || EMPTY_STR.TEXT,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "申請状況",
        Header: <TableSorting name="renewVisaDocument.status">申請状況</TableSorting>,
        Cell: ({ row }) => {
          const { status } = row.original;
          if (!status) return EMPTY_STR.TEXT;
          return <RenewVisaDocumentStatusTag status={status} />;
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "在留期限",
        Header: <TableSorting name="renewVisaDocument.residenceExpiryDate">在留期限</TableSorting>,
        Cell: ({ row }) => {
          let textColor = "inherit";
          const shouldShowAlert =
            row.original.status === RENEW_VISA_DOCUMENT_STATUS.NOT_CREATED || row.original.status === RENEW_VISA_DOCUMENT_STATUS.IN_PROGRESS;
          if (row.original.residenceExpiryDate && shouldShowAlert) {
            const expiryDate = dayjs(row.original.residenceExpiryDate);
            const now = dayjs();
            const monthsDiff = expiryDate.diff(now, "month", true);

            if (monthsDiff <= 1) {
              textColor = text.error; // Within 1 month or past
            } else if (monthsDiff <= 3) {
              textColor = button.primary; // Within 3 months
            }
          }
          return (
            <Typography variant="body14" sx={{ color: textColor }}>
              {row.original.residenceExpiryDate ? dayjs(row.original.residenceExpiryDate).format(DATE_FORMAT) : EMPTY_STR.TEXT}
            </Typography>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "申請予定日(期間更新)",
        Header: <TableSorting name="renewVisaDocument.expectSubmitDate">申請予定日(期間更新)</TableSorting>,
        Cell: ({ row }) => (
          <InputDatePicker
            enableCloseIcon={false}
            disabled={[RENEW_VISA_DOCUMENT_STATUS.COMPLETE, RENEW_VISA_DOCUMENT_STATUS.WITHDRAWN, RENEW_VISA_DOCUMENT_STATUS.REJECTED].includes(
              row.original.status,
            )}
            value={dayjs(row.original.expectSubmitDate)}
            onChange={(value) => value && !value.isSame(dayjs(row.original.expectSubmitDate)) && handleChangeExpectSubmitDate(row.original.id, value)}
          />
        ),
        muiTableBodyCellProps: {
          sx: { minWidth: "180px" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "180px" },
        },
      },
      {
        header: "申請資格",
        Header: <TableSorting name="employee.residenceStatus">申請資格</TableSorting>,
        Cell: ({ row }) => row.original.employeeResidenceStatus || EMPTY_STR.TEXT,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset", maxWidth: "200px" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset", maxWidth: "200px" },
        },
      },
      {
        header: "申請日(期間更新)",
        Cell: ({ row }) => (row.original.applicationDate ? dayjs(row.original.applicationDate).format(DATE_FORMAT) : EMPTY_STR.TEXT),
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "申請番号(期間更新)",
        Cell: ({ row }) => row.original.applicationNumber || EMPTY_STR.TEXT,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "分野",
        Cell: ({ row }) => row.original.companySpecificIndustryName || EMPTY_STR.TEXT,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "期間更新申請メモ",
        Header: <TableSorting name="renewVisaDocument.note">期間更新申請メモ</TableSorting>,
        Cell: ({ row }) => <RenewVisaNoteTextArea minRows={1} defaultNote={row.original.note ?? ""} renewVisaDocumentId={row.original.id} />,
        muiTableBodyCellProps: {
          sx: { minWidth: "300px" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "300px" },
        },
      },
      {
        header: " ",
        id: "action",
        Cell: ({ row }) => {
          const { status, id, employeeRenewVisaDocumentGroupId, employeeResidenceStatus } = row.original;
          return (
            <Button
              variant="outline"
              size="sm"
              disabled={employeeResidenceStatus === "特定技能2号"}
              onClick={async () => {
                if (employeeRenewVisaDocumentGroupId) {
                  navigate(
                    [RENEW_VISA_DOCUMENT_STATUS.COMPLETE, RENEW_VISA_DOCUMENT_STATUS.WITHDRAWN, RENEW_VISA_DOCUMENT_STATUS.REJECTED].includes(status)
                      ? getDynamicRoute(AppRoutes.renewVisaInfoHistoryDetail, {
                          employeeRenewVisaDocumentGroupId,
                        })
                      : getDynamicRoute(AppRoutes.renewVisaPresent, { employeeRenewVisaDocumentGroupId }),
                  );
                } else {
                  try {
                    const newRenewVisaDocumentGroup = await createRenewVisaDocumentGroupApi({ renewVisaDocumentIds: [toNumber(id)] });
                    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_LIST] });
                    if (
                      [RENEW_VISA_DOCUMENT_STATUS.COMPLETE, RENEW_VISA_DOCUMENT_STATUS.WITHDRAWN, RENEW_VISA_DOCUMENT_STATUS.REJECTED].includes(
                        status,
                      )
                    ) {
                      navigate(
                        getDynamicRoute(AppRoutes.renewVisaInfoHistoryDetail, {
                          employeeRenewVisaDocumentGroupId: newRenewVisaDocumentGroup.data.id,
                        }),
                      );
                    } else {
                      navigate(getDynamicRoute(AppRoutes.renewVisaPresent, { employeeRenewVisaDocumentGroupId: newRenewVisaDocumentGroup.data.id }));
                    }
                  } catch (error) {
                    showError(error);
                  }
                }
              }}
            >
              {status === RENEW_VISA_DOCUMENT_STATUS.NOT_CREATED && "作成"}
              {[RENEW_VISA_DOCUMENT_STATUS.IN_PROGRESS, RENEW_VISA_DOCUMENT_STATUS.UNDER_REVIEW, RENEW_VISA_DOCUMENT_STATUS.APPROVED].includes(
                status,
              ) && "編集"}
              {[RENEW_VISA_DOCUMENT_STATUS.COMPLETE, RENEW_VISA_DOCUMENT_STATUS.WITHDRAWN, RENEW_VISA_DOCUMENT_STATUS.REJECTED].includes(status) &&
                "確認"}
            </Button>
          );
        },
        size: 80,
      },
    ],
    [renewVisaDocumentStatuses, genderOptions],
  );

  return columns;
};
